import React from 'react';
import { isEmpty } from 'lodash';
import { focusAreaReviewTypes } from '../config';

const formatReviewPageTitle = (review, report = false) => {
  if (!isEmpty(review.reviewType)) {
    const reviewType = focusAreaReviewTypes.includes(review.reviewType)
      ? `${review.reviewType} - `
      : `${review.reviewType} `;
    const reviewId = review.reviewId && `Review ID ${review.reviewId} `;
    const reviewStatus = review.reviewStatus && `- ${review.reviewStatus}`;

    let title = `${reviewType} ${reviewId} ${reviewStatus}`;

    if (report === true) title = `Report for ${title}`;
    else if (report !== false) title = `${report} for ${title}`;

    return title;
  }

  return '';
};

const renderSubheader = grantees => {
  return !grantees ? (
    ''
  ) : grantees.length === 1 ? (
    <>
      <span style={{ paddingBottom: '0px', display: 'inline-block' }}>
        Grantee
      </span>
      : {formatReviewPageGranteeSubheading(grantees[0])}
    </>
  ) : grantees.length > 1 ? (
    <>
      <span>Grantees:</span>
      <ui>
        {grantees.map(grantee => (
          <li>{formatReviewPageGranteeSubheading(grantee)}</li>
        ))}
      </ui>
    </>
  ) : (
    ''
  );
};

const formatReviewPageGranteeSubheading = grantee => {
  const { granteeName, state, granteeId } = grantee;
  return `${
    granteeName.charAt(granteeName.length - 1) === '.'
      ? granteeName.slice(0, -1)
      : granteeName
  }, ${state} (${granteeId})`;
};

const shortenText = (string, maxLen, separator = ' ', appendix = '...') => {
  if (isEmpty(string)) return null;

  const sanitizedString = string
    .replace(/(<([^>]+)>)/gi, '')
    .replace(/&#{0,1}[a-z0-9]+;/gi, '');

  if (sanitizedString.length <= maxLen) return sanitizedString;

  return `${sanitizedString.substr(
    0,
    sanitizedString.lastIndexOf(separator, maxLen)
  )} ${appendix}`;
};

const granteeName = grantee => {
  if (grantee) {
    const granteeId = grantee.granteeId && `${grantee.granteeId}`;
    //const reviewStatus = grantee.hsesStatus && `- ${grantee.hsesStatus}`;
    const granteeName = grantee.granteeName ? `${grantee.granteeName}` : '';

    const title = `${granteeName} (${granteeId})`;

    return title;
  }

  return '';
};

const formatNumber = x => {
  if (!x) return 'N/A';

  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatSharedMember = user => {
  return `${user?.actingRole ? user?.actingRole + ' - ' : ''}${user?.fullName}`;
};

const createReportStageMessage = (reportStageInfo, stage) => {
  const sharedWithInfo = reportStageInfo?.sharedWith || [];
  const extraMessage = reportStageInfo => {
    if (reportStageInfo) {
      if (reportStageInfo.assignedTo) {
        if (stage === 'OGC' || stage === 'RO' || stage === 'OHS')
          return `${reportStageInfo.assignedTo &&
            reportStageInfo.assignedTo
              .fullName} and has been shared with ${sharedWithInfo &&
            sharedWithInfo.length > 0 &&
            sharedWithInfo.map(user => formatSharedMember(user)).join(', ')}.`;
        else {
          let toReturn = ` ${reportStageInfo.assignedTo &&
            reportStageInfo.assignedTo.fullName} and has been shared with `;
          if (sharedWithInfo?.length > 0) {
            if (sharedWithInfo.length <= 3) {
              toReturn +=
                sharedWithInfo
                  .map(user => formatSharedMember(user))
                  .join(', ') + '.';
            } else {
              const [first, second] = sharedWithInfo;
              const othersCount = sharedWithInfo.length - 2;
              toReturn += `${formatSharedMember(first)}, ${formatSharedMember(
                second
              )}, and ${othersCount} others.`;
            }
          } else {
            toReturn = reportStageInfo?.assignedTo?.fullName + '.';
          }
          return toReturn;
        }
      }
    }
  };
  switch (stage) {
    case 'signed':
      return 'The report is signed off';
    case 'finalized':
      return 'The report is finalized';
    case 'Shipped':
    case 'Grantee':
      return 'The report is shipped to Grantee';
    case 'Submit To FOM':
    case 'Submit To QC':
    case 'Return To FOM':
    case 'Return To RL':
    case 'OGC':
    case 'RO':
    case 'OHS':
    case 'Submit To Editor':
    case 'Submit To TW':
    case 'internalassigned':
    case 'internalunassigned':
      return (
        'The report is assigned to ' + extraMessage(reportStageInfo, stage)
      );
    default:
      break;
  }
};

const formatDatesArray = dates => {
  let dateText;
  if (dates.length === 1) {
    dateText = dates[0];
  } else if (dates.length === 2) {
    dateText = `${dates[0]} and ${dates[1]}`;
  } else {
    dateText =
      dates.slice(0, dates.length - 1).join(', ') +
      ', and ' +
      dates[dates.length - 1];
  }
  return dateText;
};

const formatPhoneNumber = str => {
  //Filter only numbers from the input
  let cleaned = ('' + str).replace(/\D/g, '');

  //Check if the input is of correct
  let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    //Remove the matched extension code
    //Change this to format for any country code.
    let intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }

  return null;
};

const reviewTypeFormat = reviewType => {
  switch (reviewType) {
    case 'FA-1':
      return 'fa1';
    case 'FA1-FR':
      return 'fa1fr';
    case 'FA1-PSR':
      return 'fa1-psr';
    case 'FA2-CSR':
      return 'fa2-csr';
    case 'FA-2':
      return 'fa2';
    case 'RAN':
      return 'ran';
    case 'Special':
      return 'special';
    case 'Comprehensive Review':
      return 'cr';
    case 'AIAN-DEF Reeval':
      return 'aian-def-reeval';

    case 'ERSEA':
      return 'ersea';

    case 'FA2-CR':
      return 'fa2cr';

    case 'FA1-FR':
      return 'fa1fr';
    case 'Follow-up':
      return 'followup';

    default:
      break;
  }
};

const createReportBannerMessage = (reportStageInfo, stage) => {
  const formatSharedMember = user => {
    if (!user) return '';
    return user.actingRole
      ? `${user.actingRole} - ${user.fullName}`
      : user.fullName;
  };

  const extraMessage = info => {
    const assignedUsers = info?.assignedUsers || [];
    const sharedWithInfo = info?.sharedWith || [];
    if (
      info.assignedTo?.actingRole === 'QAA' &&
      info.assignedTo?.actingRole.toLowerCase()
    ) {
      return `The report is assigned to ${
        info.assignedTo.fullName
      } and shared with ${formatSharedMember(info.assignedTo)}.`;
    }

    if (Array.isArray(assignedUsers) && assignedUsers.length > 0) {
      const assignedUsersSet = new Set(
        assignedUsers.map(
          u => `${u.firstName?.toLowerCase()}|${u.lastName?.toLowerCase()}`
        )
      );
      const filteredShared = sharedWithInfo.filter(
        u =>
          !assignedUsersSet.has(
            `${u.firstName?.toLowerCase()}|${u.lastName?.toLowerCase()}`
          )
      );
      const assignedList = assignedUsers.map(formatSharedMember).join(', ');
      const sharedList = filteredShared.map(formatSharedMember).join(', ');
      if (sharedList) {
        return `The report is assigned to ${assignedList} and shared with ${sharedList}.`;
      }
      return `The report is assigned to ${assignedList}.`;
    }
    if (!info || !Array.isArray(info.sharedWith)) return '';

    const sharedWith = info.sharedWith;
    const assignees = sharedWith.filter(
      user =>
        user?.actingRole &&
        user?.actingRole.toLowerCase() === stage.toLowerCase()
    );
    const others = sharedWith.filter(
      user =>
        !user?.actingRole ||
        user?.actingRole.toLowerCase() !== stage.toLowerCase()
    );

    let message = '';
    if (assignees.length > 0) {
      message += `The report is assigned to ${assignees
        .map(formatSharedMember)
        .join(', ')}`;
    }

    if (others.length > 0) {
      message += `${
        assignees.length > 0
          ? ' and has been shared with '
          : 'The report has been shared with '
      }${others.map(formatSharedMember).join(', ')}`;
    }

    return message ? message + '.' : '';
  };

  switch (stage) {
    case 'signed':
      return 'The report is signed off';
    case 'finalized':
      return 'The report is finalized';
    case 'Shipped':
    case 'Grantee':
      return 'The report is shipped to Grantee';
    case 'Submit To FOM':
    case 'Submit To QC':
    case 'Return To FOM':
    case 'Return To RL':
    case 'OGC':
    case 'RO':
    case 'OHS':
    case 'Submit To Editor':
    case 'Submit To TW':
    case 'internalassigned':
    case 'internalunassigned':
      return extraMessage(reportStageInfo);
    default:
      return '';
  }
};

const reviewTypeLabels = {
  'FA1-FR': 'Foundational',
  'FA2-CR': 'Comprehensive',
  'FA2-CSR': 'Comprehensive Services Review',
  'FA1-PSR': 'Program Systems Review',
  'Follow-up': 'Follow-up',
};

const ReviewTypeLabel = ({ reviewType }) => {
  const label = reviewTypeLabels[reviewType] || reviewType;
  return <>{label} Data Collection</>;
};

const isSPValidReviewType = review => {
  const excludedTypes = [
    'FA1-FR',
    'RAN',
    'FA2-CSR',
    'Follow-up',
    'Comprehensive Review',
  ];

  if (!review) return false;

  return (
    (!excludedTypes.includes(review.reviewType) &&
      review.reviewType !== 'Special') ||
    (review.reviewType === 'Special' && review.fiscalYear !== 2026)
  );
};

export {
  formatReviewPageTitle,
  renderSubheader,
  shortenText,
  granteeName,
  createReportStageMessage,
  formatNumber,
  formatDatesArray,
  formatPhoneNumber,
  reviewTypeFormat,
  createReportBannerMessage,
  ReviewTypeLabel,
  isSPValidReviewType,
};
